<template>
  <div class="image-uploader-container d-flex flex-column" style="height: 100%; width: 100%; height: calc(100vh - 71px); overflow-y: auto;">
    <v-btn icon class="back-button ma-3" style="position: absolute; top: 0; left: 0; z-index: 1000;" @click="$emit('back')">
      <lucide-arrow-left :size="24" style="width: 24px; height: 24px;"></lucide-arrow-left>
    </v-btn>

    <div style="max-width: 650px;" class="d-flex flex-column text-center align-center mx-auto pt-12">
      <h2 style="font-size: 24px;">Sube tus imágenes</h2>
      <span style="font-size: 16px;" class="my-2">Sube imágenes para convertirlas en materiales de estudio</span>
    </div>

    <div class="flex-grow-1 d-flex flex-column pa-4" style="margin: 0 auto; width: 100%;">

      <!-- Nombre del documento -->
      <div class="" style="max-width: 650px; margin: 0 auto; width: 100%;">
          <v-text-field
            v-model="documentName"
            label="Nombre del documento"
            placeholder="Introduce un nombre para tu documento"
            outlined
            dense
            hide-details
          ></v-text-field>
        </div>

      <!-- Área de carga de imágenes - siempre visible -->
      <div class="upload-area pa-6 d-flex mt-6 flex-column justify-center align-center" @click="triggerFileInput"
        @dragover.prevent @drop.prevent="onFileDrop"
        style="width: 100%; min-height: 200px; border: 1px dashed #4dd453; border-radius: 8px; cursor: pointer; max-width: 650px; margin: 0 auto;">
        <input ref="fileInput" type="file" class="d-none" @change="onFileSelected" accept=".png,.jpg,.jpeg,.gif,.webp"
          multiple />
        <v-avatar color="transparent" :size="64" class="mb-4">
          <lucide-image color="#4dd453" :size="32" style="width: 32px;"></lucide-image>
        </v-avatar>
        <h3 class="mb-2" style="font-weight: 500; font-size: 1rem;">Arrastra tus imágenes aquí</h3>
        <p class="text-body-2 grey--text text--darken-1 mb-3">
          PNG, JPG, JPEG, GIF, WEBP
        </p>
        <v-btn depressed @click.stop="triggerFileInput" text style="color: #4dd453; font-size: 0.9rem;"
          class="font-rubik mt-3">
          <span>Seleccionar Imágenes</span>
        </v-btn>
      </div>

      <!-- Previsualización de imágenes seleccionadas - visible solo si hay imágenes -->
      <div v-if="selectedImages.length > 0"
         class="selected-images-container d-flex flex-column mt-8" style="flex: 1; border-top: 1px solid #dedede;">
        <div class="d-flex justify-space-between align-center my-4">
          <span style="font-size: 0.8rem;">{{ selectedImages.length }} {{ selectedImages.length === 1 ? 'imagen seleccionada' :
            'imágenes seleccionadas' }}</span>
          <v-btn text color="error" @click="clearImages">Borrar todas</v-btn>
        </div>


        <div class="d-flex flex-column images-container pa-4 rounded-xl" style="flex: 1;">
          <div class="pages-grid d-flex flex-wrap rounded-xl pb-4">
            <div v-for="(image, index) in selectedImages" :key="index" class="page-item pa-2" style="height: fit-content;">
              <v-card outlined class="image-card">
                <v-btn 
                style="position: absolute; top: 0; right: 0; z-index: 1000;"
                icon small @click="removeImage(index)"
                class="white"
                >
                      <lucide-x :size="16"></lucide-x>
                    </v-btn>

                <v-img :src="image.preview" aspect-ratio="1" class="transparent" contain>
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <v-card-text class="pa-2">
                  <div class="d-flex justify-space-between align-center">
                    <div class="text-truncate" style="max-width: 80%">{{ image.file.name }}</div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </div>

       <!-- Study Configurator Component -->
      <div v-if="selectedImages.length > 0" style="max-width: 650px; margin: 24px auto 0; width: 100%;">
         <h2 style="font-size: 24px; text-align: center; margin-bottom: 16px;">Configuración del estudio</h2>
         <span style="font-size: 16px; text-align: center; display: block;" class="my-2">Escoje el lenguage y los tipos de preguntas.</span>
        <study-configurator ref="studyConfig" :is-premium="isPremium" @show-premium-dialog="handleUpgrade" />
      </div>

    </div>

    <!-- Botón fijo en la esquina inferior derecha - visible solo si hay imágenes -->
    <div v-if="selectedImages.length > 0" class="pa-4 d-flex justify-end fixed-button-container" style="border-top: 1px solid #dedede; margin: 0 auto; width: 100%;">
      <v-btn depressed color="primary" class="primary-btn" @click="uploadImages" :loading="isUploading"
        :disabled="isUploading || selectedImages.length === 0 || !documentName.trim()">
        Generar materiales
      </v-btn>
    </div>

    <v-dialog v-model="showProcessingDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="text-h5 text-center pa-4">
          {{ isUploading ? 'Subiendo imágenes' : 'Generando materiales' }}
        </v-card-title>
        <v-card-text class="text-center">
          <v-progress-circular indeterminate color="primary" size="64" width="5" class="mb-4"></v-progress-circular>
          <p class="mt-4" v-if="isUploading">Estamos subiendo tus imágenes. Por favor, espera...</p>
          <p class="mt-4" v-else>Estamos procesando tus imágenes y generando materiales de estudio. Esto puede tardar
            unos
            segundos...</p>
        </v-card-text>
      </v-card>
    </v-dialog>

     <features-dialog :showDialog.sync="showFeaturesDialog" />
  </div>
</template>

<script>
import { ArrowLeft, Image, X } from 'lucide-vue'
import axios from 'axios'
import StudyConfigurator from './StudyConfigurator.vue';
import FeaturesDialog from '@/components/premium/FeaturesDialog.vue';

export default {
  name: 'ImageUploader',
  components: {
    LucideArrowLeft: ArrowLeft,
    LucideImage: Image,
    LucideX: X,
    StudyConfigurator,
    FeaturesDialog
  },
  data() {
    return {
      selectedImages: [],
      documentName: '',
      isUploading: false,
      isProcessing: false,
      showProcessingDialog: false,
      uploadError: null,
      showFeaturesDialog: false,
    }
  },
   computed: {
    isPremium() {
      return this.$root.currentUser && this.$root.currentUser.premium;
    },
  },
  methods: {
    handleUpgrade(){
      this.showFeaturesDialog = true;
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    onFileDrop(event) {
      event.preventDefault()
      const files = event.dataTransfer.files
      this.processFiles(files)
    },

    onFileSelected(event) {
      const files = event.target ? event.target.files : event;
      if (!files || files.length === 0) return;
      this.processFiles(files);
    },

    processFiles(files) {
      console.log('processFiles llamado', files);
      // Si no es un objeto iterable, convertirlo a array
      let filesArray;
      if (files.item) {
        // Si es un FileList
        filesArray = Array.from(files);
      } else if (Array.isArray(files)) {
        // Si ya es un array
        filesArray = files;
      } else {
        // Si es un solo archivo
        filesArray = [files];
      }

      filesArray.forEach(file => {
        // Verificar si es una imagen válida
        if (!file.type.match('image.*')) {
          this.$toast.error(`${file.name} no es una imagen válida`);
          return;
        }

        // Crear previsualización
        const reader = new FileReader();
        reader.onload = (e) => {
          this.selectedImages.push({
            file: file,
            preview: e.target.result
          });
        };
        reader.readAsDataURL(file);
      });

      // Si no hay nombre de documento, crear uno basado en la primera imagen
      if (!this.documentName.trim() && filesArray.length > 0) {
        const firstFileName = filesArray[0].name;
        // Extraer nombre base sin extensión
        const baseName = firstFileName.split('.').slice(0, -1).join('.');
        this.documentName = baseName || 'Imágenes';
      }
    },

    removeImage(index) {
      this.selectedImages.splice(index, 1)
    },

    clearImages() {
      this.selectedImages = []
    },

    async uploadImages() {
      if (this.selectedImages.length === 0 || !this.documentName.trim()) return

      this.isUploading = true
      this.showProcessingDialog = true

      try {
        const formData = new FormData()

        // Determinar el nombre del documento
        const originalName = this.documentName.trim();

        // Añadir todas las imágenes al FormData
        console.log(`Preparando ${this.selectedImages.length} imágenes para subir`);
        this.selectedImages.forEach((image, index) => {
          // Usar 'image' como nombre de campo para evitar problemas con multer
          formData.append('image', image.file, image.file.name);
          console.log(`Imagen #${index+1} añadida:`, image.file.name, image.file.type, image.file.size);
        })

        formData.append('user_id', this.$root.currentUser.userId)
        formData.append('contentTypes', JSON.stringify(['flashcards', 'quiz', 'summary']))

        // Get config from StudyConfigurator
        const config = this.$refs.studyConfig.getConfig();
        formData.append('language', config.selectedLanguage);
        formData.append('questionTypes', JSON.stringify(config.selectedQuestionTypes));

        formData.append('original_name', originalName)

        // Debug: Mostrar entradas del FormData
        console.log('Contenido del FormData:');
        for (let [key, value] of formData.entries()) {
          if (value instanceof File) {
            console.log(`- ${key}: File(${value.name}, ${value.type}, ${value.size} bytes)`);
          } else {
            console.log(`- ${key}: ${value}`);
          }
        }

        console.log('Enviando solicitud al servidor...');
        const response = await axios.post('/image-content', formData, {
          headers: {
            "Authorization": this.$root.token.value
          }
        })

        console.log('Respuesta recibida:', response.data);
        if (response.data.success) {
          const documentId = response.data.documentId
          this.$toast.success('¡Imágenes subidas con éxito!');

          // Navegar a la página del documento
          this.$router.push(`/app/document/${documentId}`)
        } else {
          this.$toast.warning('Las imágenes se subieron, pero hubo un problema al generar el contenido')
        }
      } catch (error) {
        console.error('Error al subir imágenes:', error);
        // Mostrar más detalles del error
        if (error.response) {
          console.error('Respuesta del servidor:', error.response.data);
          this.$toast.error(`Error del servidor: ${error.response.data.error || error.response.statusText}`);
        } else if (error.request) {
          console.error('No se recibió respuesta del servidor');
          this.$toast.error('No se pudo conectar con el servidor. Revisa tu conexión a internet.');
        } else {
          this.$toast.error(`Error al procesar las imágenes: ${error.message}`);
        }
      } finally {
        this.isUploading = false
        this.showProcessingDialog = false
      }
    },

    onImagesSelected(images) {
      console.log('onImagesSelected llamado con:', images);
      // This method is called from the parent Upload component
      // when images are dragged onto the main window
      if (!images) {
        console.error('No se recibieron imágenes en onImagesSelected');
        return;
      }

      // Log detailed information about what was received
      console.log('Tipo de imágenes recibido:', typeof images);
      if (typeof images === 'object') {
        console.log('¿Es un array?', Array.isArray(images));
        console.log('¿Es un FileList?', images instanceof FileList);
        console.log('Número de elementos:', images.length || 'No tiene propiedad length');
        console.log('Propiedades del objeto:', Object.keys(images));
        
        // Si es un objeto pero no un array o FileList, podría ser un único archivo
        if (!Array.isArray(images) && !(images instanceof FileList) && images.type) {
          console.log('Parece ser un único archivo:', images.name, images.type);
          // Convertir a array de un solo elemento
          images = [images];
        }
      }

      // Convert to array if not already
      let imagesArray;
      if (images.item && typeof images.item === 'function') {
        // If it's a FileList-like object
        imagesArray = Array.from(images);
        console.log('Convertido FileList a array con', imagesArray.length, 'elementos');
      } else if (Array.isArray(images)) {
        // If it's already an array
        imagesArray = images;
        console.log('Ya es un array con', imagesArray.length, 'elementos');
      } else if (images.files && images.files.length > 0) {
        // If it contains a 'files' property (like event.dataTransfer)
        imagesArray = Array.from(images.files);
        console.log('Extrayendo files de dataTransfer con', imagesArray.length, 'elementos');
      } else {
        // If it's a single file object
        imagesArray = [images];
        console.log('Tratando como archivo único');
      }

      // Verify each file is valid
      const validImages = imagesArray.filter(file => {
        if (!file || !file.type) {
          console.error('Archivo inválido encontrado:', file);
          return false;
        }
        
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          console.warn('Archivo no es una imagen:', file.name, file.type);
        }
        return isImage;
      });

      console.log(`De ${imagesArray.length} archivos, ${validImages.length} son imágenes válidas`);

      if (validImages.length === 0) {
        this.$toast.error('No se encontraron imágenes válidas');
        return;
      }

      this.processFiles(validImages);
      
      // Desactivar autoSelectingDocument después de procesar
      this.$emit('disable-auto-selecting-document');
    }
  }
}
</script>

<style scoped>
.image-uploader-container {
  background-color: white;
  position: relative;
  padding-bottom: 70px; /* Espacio para el botón fijo */
}

.image-card {
  position: relative;
  transition: transform 0.2s ease;
  height: auto;
  border-radius: 8px !important;
  overflow: hidden;
  width: 150px;
}

.image-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.primary-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 800 !important;
  font-size: 0.9rem !important;
  border-radius: 13px !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  height: 38px !important;
  max-height: 38px !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
  border: 2px solid #4dd453 !important;
  background-color: #4dd453 !important;
  box-shadow: 0 4px 0 #42b848 !important;
  color: white;
}

.primary-btn:hover {
  filter: brightness(1.02);
  transform: translateY(0) !important;
}

.primary-btn:active {
  box-shadow: 0 0 0 #42b848 !important;
  transform: translateY(4px) !important;
}

.primary-btn:disabled {
  border: 2px solid #E0E0E0 !important;
  box-shadow: 0 4px 0 #9a9a9a !important;
}

.images-container {
  background-color: #F9F9F9;
  opacity: 0.8;
  background-image: radial-gradient(#8cb785a9 0.8500000000000001px, #F9F9F9 0.8500000000000001px);
  background-size: 17px 17px;
  background-repeat: repeat;
  justify-content: center;
  border-radius: 8px;
  min-height: 200px;
  margin-bottom: 20px;
}

.upload-area {
  border: 1px dashed #4dd453;
  border-radius: 8px;
  text-align: center;
  transition: background-color 0.3s ease;
}

.upload-area:hover {
  background-color: rgba(76, 175, 80, 0.1);
}

.v-text-field>>>.v-input__slot {
  min-height: 42px !important;
}

.v-text-field>>>.v-text-field__slot input {
  font-size: 14px;
  font-family: "Nunito", "DINRounded", "Inter", sans-serif !important;
}

.v-card-text {
  padding: 12px !important;
}

.fixed-button-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 10;
}

.pages-grid {
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
  margin: 0;
  min-height: 200px;
  background-color: #F9F9F9;
  opacity: 0.8;
  background-image: radial-gradient(#8cb785a9 0.8500000000000001px, #F9F9F9 0.8500000000000001px);
  background-size: 17px 17px;
  background-repeat: repeat;
  justify-content: center;
}

.page-item {
  flex: 0 0 auto;
  width: 150px;
  margin: 8px;
  border-radius: 8px !important;
}

@media (max-width: 960px) {
  .page-item {
    width: 150px;
  }
}

@media (max-width: 600px) {
  .page-item {
    width: 150px;
  }
}

.v-img {
  height: 150px !important;
  width: 150px !important;
}
</style>